<template>
  <div>
    <div class="grid">
      <div class="grid-title">
        <div class="row">
          <div class="col-md-10 heading header-row">
            <h4>
              Low Stock Fabrics
              <span
                class="fabric-calculation-info-container bg-primary no-inherit-styles"
              >
                <FabricCalculationInfo>
                  <template #title
                    >This table shows the fabrics that are low on stock.</template
                  >
                  <template #content>
                    <div class="calculation-info-container">
                      <div class="title">Stock Open Roll</div>
                      <div class="description">
                        The currennt stock level of the fabric roll in use.
                      </div>
                    </div>
                    <div class="calculation-info-container">
                      <div class="title">Stock < 30 m</div>
                      <div class="description">
                        Will display a text in red saying "Check in roll" when
                        the total stock of the fabric in the warehouse is below
                        30 meters.
                      </div>
                    </div>
                    <div class="calculation-info-container">
                      <div class="title">Override Status</div>
                      <div class="description">
                        Indicates the current
                        <b>availability_override</b> status of the fabric.
                        <br /><br />
                        <b>Possible statuses:</b>
                        <div>
                          -
                          <span style="color: red">Update Override (Red)</span>:
                          An override exists, but its value has dropped to zero.
                        </div>
                        <div>
                          -
                          <span style="color: green"
                            >Override Present (Green)</span
                          >: An active override exists with a value greater than
                          zero.
                        </div>
                        <div>
                          -
                          <span style="color: goldenrod"
                            >Check Override (Yellow)</span
                          >: No override currently exists for this fabric.
                        </div>
                      </div>
                    </div>
                  </template>
                </FabricCalculationInfo>
              </span>
            </h4>
          </div>
          <div class="col-md-2 text-right">
            <!-- Filter dropdown -->
            <select
              v-model="selectedFilter"
              class="form-control w-auto d-inline-block"
            >
              <option value="check_in_roll">Check in roll</option>
              <option value="update_override">Update Override</option>
              <option value="check_override">Check Override</option>
              <option value="soon_sold_out">Status = soon sold out</option>
              <option value="">All</option>
            </select>
          </div>
        </div>
      </div>

      <div class="grid-body">
        <div class="fabric-low-stock-table-div">
          <vue-good-table
            :columns="columns"
            :rows="filteredRows"
            styleClass="table table-bordered"
            :pagination-options="{ enabled: false }"
            :search-options="{ enabled: false }"
          >
            <template slot="table-row" slot-scope="props">
              <span
                v-if="
                  props.column.field === 'stock_open_roll' &&
                  props.row.length > 0
                "
              >
                <Progress
                  :val="props.row.percentage_used"
                  :label="
                    round(props.row.length_remaining) +
                    ' ' +
                    props.row.unit +
                    ' / ' +
                    round(props.row.length) +
                    ' ' +
                    props.row.unit +
                    ' (length used = ' +
                    round(props.row.length_used) +
                    ' ' +
                    props.row.unit +
                    ')'
                  "
                />
              </span>

              <span v-else-if="props.column.field == 'fabric_status'">
                <span
                  :class="
                    'badge badge-' + getStatusColor(props.row.fabric_status)
                  "
                >
                  {{ props.row.fabric_status.replace(/_/g, " ") }}
                </span>
              </span>

              <span
                v-else-if="
                  props.column.field === 'notification_low_stock' &&
                  props.row.total_stock_m < 30
                "
              >
                <p class="fabric-low-stock-status-alert bg-danger text-white">
                  Check in roll
                </p>
              </span>

              <span
                v-else-if="
                  props.column.field === 'override_status' &&
                  props.row.total_stock_m < 30
                "
              >
                <p
                  v-if="
                    props.row.already_overriden && props.row.override_count == 0
                  "
                  class="fabric-low-stock-status-alert bg-danger text-white"
                >
                  Update override
                </p>
                <p
                  v-if="
                    props.row.already_overriden && props.row.override_count > 0
                  "
                  class="fabric-low-stock-status-alert bg-success text-white"
                >
                  Override present
                </p>
                <p
                  v-if="!props.row.already_overriden"
                  class="fabric-low-stock-status-alert fabric-low-stock-bg-warning text-white"
                >
                  Check override
                </p>
              </span>

              <span v-else>
                {{ props.formattedRow[props.column.field] }}
              </span>
            </template>
          </vue-good-table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { VueGoodTable } from "vue-good-table";
import Progress from "@/pages/Tools/progress-bar.vue";
import FabricCalculationInfo from "../dashboardComponents/FabricCalculationInfo";

export default {
  name: "FabricLowStock",
  components: {
    VueGoodTable,
    Progress,
    FabricCalculationInfo,
  },
  data() {
    return {
      rows: [],
      selectedFilter: "check_in_roll",
      columns: [
        {
          label: "Fabric ID",
          field: "fabric_id",
          thClass: "sticky-top",
          tdClass: "col-fabric-id",
        },
        { label: "Fabric Name", field: "fabric_name", thClass: "sticky-top" },
        { label: "Roll Code", field: "roll_code", thClass: "sticky-top" },
        {
          label: "Stock Open Roll",
          field: "stock_open_roll",
          thClass: "sticky-top",
          tdClass: "col-stock-open-roll",
          sortable: true,
          sortFn: (x, y, col, rowX, rowY) => {
            // Sort by percentage_used
            const percentageX = rowX.percentage_used || 0;
            const percentageY = rowY.percentage_used || 0;
            return percentageX < percentageY
              ? -1
              : percentageX > percentageY
              ? 1
              : 0;
          },
        },
        {
          label: "Status",
          field: "fabric_status",
          thClass: "sticky-top text-center",
          tdClass: "text-center",
          sortable: true,
        },
        {
          label: "Rolls in warehouse",
          field: "roll_count",
          thClass: "sticky-top",
          sortable: true,
        },
        {
          label: "Stock in warehouse (m)",
          field: "total_stock_m",
          thClass: "sticky-top",
          sortable: true,
        },
        {
          label: "Stock < 30 m",
          field: "notification_low_stock",
          thClass: "sticky-top",
          sortable: true,
          sortFn: (x, y, col, rowX, rowY) => {
            // Get the display text for sorting
            const getDisplayText = (row) => {
              if (row.total_stock_m < 30) {
                return "Check in roll";
              }
              return ""; // or some default text
            };

            const textX = getDisplayText(rowX);
            const textY = getDisplayText(rowY);
            return textX.localeCompare(textY);
          },
        },
        {
          label: "Override status",
          field: "override_status",
          thClass: "sticky-top",
          sortable: true,
          sortFn: (x, y, col, rowX, rowY) => {
            // Get the display text for sorting
            const getDisplayText = (row) => {
              if (row.total_stock_m < 30) {
                if (row.already_overriden && row.override_count == 0) {
                  return "Update override";
                }
                if (row.already_overriden && row.override_count > 0) {
                  return "Override present";
                }
                if (!row.already_overriden) {
                  return "Check override";
                }
              }
              return ""; // or some default text
            };

            const textX = getDisplayText(rowX);
            const textY = getDisplayText(rowY);
            return textX.localeCompare(textY);
          },
        },
      ],
    };
  },
  mounted() {
    this.fetchAllFabrics();
  },
  computed: {
    filteredRows() {
      if (!this.selectedFilter) return this.rows;

      return this.rows.filter((row) => {
        if (this.selectedFilter === "check_in_roll") {
          return row.total_stock_m < 30 && row.total_stock_m > 0;
        }
        if (this.selectedFilter === "update_override") {
          return (
            row.total_stock_m < 30 &&
            row.total_stock_m > 0 &&
            row.already_overriden &&
            row.override_count == 0
          );
        }
        if (this.selectedFilter === "check_override") {
          return (
            row.total_stock_m < 30 &&
            row.total_stock_m > 0 &&
            !row.already_overriden
          );
        }
        if (this.selectedFilter === "soon_sold_out") {
          return (
            [
              "soon_sold_out_not_coming_back",
              "soon_sold_out_coming_back",
            ].includes(row.fabric_status) && row.total_stock_m > 0
          );
        }
        return true;
      });
    },
  },
  methods: {
    async fetchAllFabrics() {
      try {
        const res = await axios.get(
          "/v2/fabrics/availability_override/low_stock?columns=*"
        );
        this.rows = res.data.data.collection;
      } catch (error) {
        console.error("Failed to fetch fabrics", error);
      }
    },
    getStatusColor(status) {
      if (["available"].includes(status)) return "primary";
      if (
        ["soon_sold_out_not_coming_back", "soon_sold_out_coming_back"].includes(
          status
        )
      )
        return "warning";
      return "danger";
    },
    round(value) {
      return Number(Math.round(value + "e" + 2) + "e-" + 2);
    },
  },
};
</script>
<style>
.fabric-low-stock-status-alert {
  text-align: center;
  padding: 5px;
  margin: 0;
  width: 100px;
  border-radius: 3px;
}
.fabric-low-stock-bg-warning {
  background-color: #fdd01c !important;
}
.fabric-low-stock-table-div {
  overflow: scroll;
  height: 600px;
}
.fabric-low-stock-table-div th.sticky-top {
  position: sticky !important;
  top: -1px;
}
.col-stock-open-roll {
  min-width: 250px;
  max-width: 350px;
}
.col-fabric-id {
  min-width: 90px;
  max-width: 120px;
}
.fabric-calculation-info-container {
  display: inline-block;
  margin-left: 5px;
  padding: 6px;
  border-radius: 8px;
}
</style>
